/**
 * SAM-198 — is a service connected to the booking engine (sam-booking-service)?
 *
 * Mirrors `engine_sync.is_fully_mapped` (sam-lambda-functions): a service counts as synced only with
 * an engine service id AND at least one resource — `booking_engine_resource_id` or a non-empty
 * `booking_engine_resource_map` (SAM-540). A sync that created the engine Service and then failed on
 * the Resource leaves a row that looks mapped but cannot answer for slots, so it is not synced.
 *
 * Where the answer comes from:
 * - GET /bookings/services/{id}/engine-sync (`synced`) is the backend's own verdict; it wins.
 * - Otherwise the mapping columns on the row (the /crud/service list returns them).
 * - The editor's /services/description read does not select them, so with neither source the state
 *   is 'unknown' rather than a misleading "not synced".
 */

export type EngineSyncState = 'synced' | 'unsynced' | 'unknown';

export interface EngineMappingFields {
  booking_engine_service_id?: string | null;
  booking_engine_resource_id?: string | null;
  booking_engine_resource_map?: unknown;
}

const parseMap = (value: unknown): Record<string, unknown> | null => {
  let v = value;
  if (typeof v === 'string') {
    if (!v.trim()) return null;
    try { v = JSON.parse(v); } catch { return null; }
  }
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
};

/** `engine_sync.is_fully_mapped` on a service row. */
export function isEngineMapped(row: EngineMappingFields | null | undefined): boolean {
  if (!row?.booking_engine_service_id) return false;
  if (row.booking_engine_resource_id) return true;
  const map = parseMap(row.booking_engine_resource_map);
  return !!map && Object.keys(map).length > 0;
}

export function engineSyncState(
  row: EngineMappingFields | null | undefined,
  status?: { synced?: boolean | null } | null,
): EngineSyncState {
  if (status && typeof status.synced === 'boolean') return status.synced ? 'synced' : 'unsynced';
  if (!row || !('booking_engine_service_id' in row)) return 'unknown';
  return isEngineMapped(row) ? 'synced' : 'unsynced';
}
